import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2, ExternalLink, ShieldCheck, Gift, ArrowRight } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { SectionHeading, EmptyState } from "@/components/shared/SectionHeading";
import { RiskBanner } from "@/components/shared/RiskBanner";
import { Button } from "@/components/ui/button";

const benefitLabel = (b) => {
  if (!b || b.type === "none") return null;
  const map = { discount: `${b.discount_percent}% VIP discount`, free_first_month: "Free first month VIP", free_duration: `${b.duration_days} days free VIP`, full_free: "Full free VIP" };
  return b.description || map[b.type];
};

export default function Brokers() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  useSEO("Partner Brokers", "Open a trading account with INVIXA's verified partner brokers and unlock member benefits.");
  const [brokers, setBrokers] = useState(null);

  useEffect(() => { api.get("/brokers").then((r) => setBrokers(r.data)).catch(() => setBrokers([])); }, []);

  return (
    <div>
      <section className="radial-cyan">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-20 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-cyan/30 bg-brand-cyan/10 px-4 py-1.5 text-xs font-mono uppercase tracking-widest text-brand-cyan"><Building2 className="h-4 w-4" /> {t("partner_brokers")}</span>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl font-extrabold text-white">Verified Partner Brokers</h1>
          <p className="mt-5 max-w-2xl mx-auto text-slate-400">Open an account through a partner broker, submit your verification, and unlock configurable VIP benefits once approved.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pb-16">
        {brokers === null ? <div className="text-center text-slate-500 py-20">{t("loading")}</div>
          : brokers.length === 0 ? <EmptyState message="Partner brokers will be listed here soon." icon={Building2} />
          : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {brokers.map((b) => {
                const bl = benefitLabel(b.benefit);
                return (
                  <div key={b.id} className="card-surface hover-glow rounded-2xl p-6 flex flex-col" data-testid={`broker-card-${b.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="h-14 w-14 rounded-xl bg-brand-elevated flex items-center justify-center overflow-hidden shrink-0">
                        {b.logo_file_id ? <img src={fileUrl(b.logo_file_id)} alt={b.name} className="h-full w-full object-contain p-1.5" /> : <Building2 className="h-6 w-6 text-slate-500" />}
                      </div>
                      <div>
                        <h3 className="font-display text-lg font-semibold text-white">{b.name}</h3>
                        {b.regulation && <p className="text-xs text-slate-500">{b.regulation}</p>}
                      </div>
                    </div>
                    {b.description && <p className="mt-4 text-sm text-slate-400 line-clamp-3">{b.description}</p>}
                    <div className="mt-4 grid grid-cols-2 gap-2 text-xs">
                      {b.min_deposit && <div className="rounded-lg bg-brand-navy/50 p-2"><span className="text-slate-500">Min deposit</span><p className="text-white font-medium">{b.min_deposit}</p></div>}
                      {b.leverage && <div className="rounded-lg bg-brand-navy/50 p-2"><span className="text-slate-500">Leverage</span><p className="text-white font-medium">{b.leverage}</p></div>}
                    </div>
                    {bl && <div className="mt-4 flex items-center gap-2 rounded-lg border border-brand-green/20 bg-brand-green/5 px-3 py-2 text-xs text-brand-green"><Gift className="h-4 w-4" /> {bl}</div>}
                    <div className="mt-5 pt-5 border-t border-white/10 flex gap-2">
                      {b.open_account_url && <a href={b.open_account_url} target="_blank" rel="noopener noreferrer" className="flex-1"><Button className="w-full gradient-brand text-brand-navy font-semibold" data-testid={`broker-open-${b.id}`}>Open Account <ExternalLink className="h-4 w-4 ml-1.5" /></Button></a>}
                      <Button variant="outline" className="border-white/15 text-white" onClick={() => navigate(user ? "/dashboard/broker-verification" : "/login")} data-testid={`broker-verify-${b.id}`}><ShieldCheck className="h-4 w-4 mr-1.5" /> Verify</Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
      </section>
      <RiskBanner />
    </div>
  );
}
